import { countBy, maxBy, round, uniq } from 'es-toolkit'
import { Duration } from 'luxon'
import { ceil } from './math'

// G0 (travel) moves are always executed at a fixed speed.
export const G0_SPEED_MM_S = 200

// Extrusion (G1) moves are actually run 20% faster than the speed set by the
// user, so the effective print speed is `printSpeed * EXTRUSION_SPEED_FACTOR`.
export const EXTRUSION_SPEED_FACTOR = 1.2

// Arbitrary safety margin added on top of the theoretical print duration, to
// account for everything the gcode geometry does not model (accelerations,
// pauses, operator handling…). Purely empirical: no formula backs the 20%.
export const PRINT_DURATION_MARGIN_FACTOR = 1.2

export type StatsCumulatives = {
  // Travel distances (mm) at each gcode index, split by move type. Each move is
  // measured against the previous move of ANY type (prevMove), so a G1 segment
  // starts wherever the head actually is — including at the end of a G0 travel.
  // g1DistMm is therefore the real extruded length (see getMaterialLengthM) and
  // printing time can be recomputed in O(1) whenever the print speed changes.
  g0DistMm: Float64Array
  g1DistMm: Float64Array
  // Highest Z (m) reached at each gcode index — the printed height so far, used
  // for the average lift speed.
  maxZM: Float64Array
}

/**
 * Single O(n) pass over the whole gcode. Reading any statistic for a given
 * `progress` is then O(1) (see the getters below), instead of re-scanning the
 * whole gcode on every progress change.
 */
export const computeStatsCumulatives = (
  gcode: ParsedGcodeLine[],
): StatsCumulatives => {
  const n = gcode.length
  const g0DistMm = new Float64Array(n)
  const g1DistMm = new Float64Array(n)
  const maxZM = new Float64Array(n)

  let g0Running = 0
  let g1Running = 0
  let maxZ = 0
  let prevMove: ParsedGcodeGLine | undefined

  for (let i = 0; i < n; i++) {
    const line = gcode[i]
    const isG0 = line.method === 'G0'
    const isG1 = line.method === 'G1'

    if ((isG0 || isG1) && 'x' in line) {
      // Distance from the previous move of ANY type, accumulated separately so
      // G0/G1 speeds can be applied later. Measuring against prevMove (not the
      // previous G1) is what makes g1DistMm the true extruded length: a G1
      // segment starts where the head currently is, so if a G0 travel precedes
      // it, only the post-travel G1 stretch is deposited — the travel gap must
      // NOT be counted as material.
      if (prevMove) {
        const distMm = Math.hypot(
          line.x - prevMove.x,
          line.y - prevMove.y,
          line.z - prevMove.z,
        ) * 1000
        if (isG0) g0Running += distMm
        else g1Running += distMm
      }
      if (line.z > maxZ) maxZ = line.z
      prevMove = line
    }

    g0DistMm[i] = g0Running
    g1DistMm[i] = g1Running
    maxZM[i] = maxZ
  }

  return { g0DistMm, g1DistMm, maxZM }
}

// Clamp an arbitrary progress (which can transiently be negative, e.g. after a
// rewind, or exceed the last index) to a valid array index.
const clampIndex = (progress: number, length: number) =>
  Math.min(Math.max(progress, 0), length - 1)

/**
 * Extruded material length (m, rounded up) revealed at `progress`. This is the
 * cumulative G1 distance (mm → m): only stretches actually deposited count, so
 * G0 travels are excluded rather than bridged over.
 */
export const getMaterialLengthM = (
  cumulatives: StatsCumulatives,
  progress: number,
): number => {
  const { g1DistMm } = cumulatives
  if (g1DistMm.length === 0) return 0
  return ceil(g1DistMm[clampIndex(progress, g1DistMm.length)] / 1000)
}

/**
 * Total printing time (seconds) up to `progress` for a given print speed,
 * including the PRINT_DURATION_MARGIN_FACTOR safety margin.
 */
export const getPrintingTimeSeconds = (
  cumulatives: StatsCumulatives,
  progress: number,
  printSpeedMmS: number,
): number => {
  const { g0DistMm, g1DistMm } = cumulatives
  if (g0DistMm.length === 0) return 0
  const idx = clampIndex(progress, g0DistMm.length)
  return (
    (g0DistMm[idx] / G0_SPEED_MM_S
      + g1DistMm[idx] / (printSpeedMmS * EXTRUSION_SPEED_FACTOR))
    * PRINT_DURATION_MARGIN_FACTOR
  )
}

/** Format a duration in seconds down to whole hours + minutes (seconds dropped). */
export const formatPrintingTime = (totalSeconds: number): Duration =>
  Duration.fromObject({ seconds: totalSeconds })
    .shiftTo('hours', 'minutes', 'seconds')
    .set({ seconds: 0 })
    .shiftTo('hours', 'minutes')

/**
 * Average lift (vertical build) speed in cm/h up to `progress`: the printed
 * height reached divided by the time taken to reach it.
 */
export const getLiftSpeedCmPerH = (
  cumulatives: StatsCumulatives,
  progress: number,
  printSpeedMmS: number,
): number => {
  const timeSeconds = getPrintingTimeSeconds(cumulatives, progress, printSpeedMmS)
  if (timeSeconds <= 0) return 0
  const heightCm = cumulatives.maxZM[clampIndex(progress, cumulatives.maxZM.length)] * 100
  return ceil(heightCm / (timeSeconds / 3600))
}

/**
 * Layer height (mm) derived from the gcode itself, as the most common positive
 * gap between consecutive Z heights. Used as a fallback when the slicer header
 * comment is missing or unparseable, so volume-based stats never silently
 * collapse to 0.
 */
export const getLayerHeightFromZSteps = (gcode: ParsedGcodeLine[]): number => {
  const zHeightsMm = uniq(
    gcode
      .filter((line): line is ParsedGcodeGLine => 'z' in line)
      .map(line => round(line.z * 1000, 3)),
  ).sort((a, b) => a - b)

  const gapCounts = countBy(
    zHeightsMm
      .map((z, idx) => (idx === 0 ? 0 : round(z - zHeightsMm[idx - 1], 3)))
      .filter(gap => gap > 0),
    gap => String(gap),
  )

  const mostCommonGap = maxBy(Object.keys(gapCounts), gap => gapCounts[gap])

  return mostCommonGap ? Number(mostCommonGap) : NaN
}

/** Layer height (mm): from the slicer comment when available, else derived. */
export const getGcodeLayerHeight = (gcode: ParsedGcodeLine[]): number => {
  const layerHeightLine = gcode.find(line =>
    line.originalLine.startsWith('// Layer height (mm)'),
  )
  const commentValue
    = (layerHeightLine?.originalLine ?? '').split(':').at(-1) ?? ''
  const layerHeightFromComment = parseFloat(commentValue.trim())

  if (Number.isFinite(layerHeightFromComment) && layerHeightFromComment > 0) {
    return layerHeightFromComment
  }

  return getLayerHeightFromZSteps(gcode)
}

/**
 * Fresh mortar volume (m³), i.e. the deposited bead volume, modelling each bead
 * as a rectangular section (height = layer height, width = bead width).
 */
export const getPrintVolumeM3 = (
  materialLengthM: number,
  layerHeightMm: number,
  layerWidthMm: number,
): number =>
  materialLengthM * (layerHeightMm * 0.001) * (layerWidthMm * 0.001)

// Notations :
//   V -> volume de mortier frais (m³) = volume du cordon déposé (getPrintVolumeM3)
//   p -> masse de matière sèche (kg)
//   d -> densité du matériau (kg/m³)
//   k -> pourcentage d'eau (par rapport à p)
// Le mortier frais = matière sèche + eau. En volume, l'eau représente k% du
// volume sec, donc : V = (p/d) * (1 + k/100)  <=>  p = V*d / (1 + k/100).
/** Dry material mass (kg) required to print the given fresh mortar volume. */
const rawDryMassKg = (volumeM3: number, material: MaterialStat): number =>
  (volumeM3 * material.densityKgM3) / (1 + material.waterPercent / 100)

/** Dry material mass (kg, rounded up) required for the given volume. */
export const getMaterialQuantityKg = (
  volumeM3: number,
  material?: MaterialStat,
): number => (material ? ceil(rawDryMassKg(volumeM3, material)) || 0 : 0)

/**
 * Fresh mortar mass (kg, rounded up) for the given volume: dry material + water.
 */
export const getFreshMortarMassKg = (
  volumeM3: number,
  material?: MaterialStat,
): number => {
  if (!material) return 0
  const dryKg = rawDryMassKg(volumeM3, material)
  const waterKg = dryKg * (material.waterPercent / 100)
  return ceil(dryKg + waterKg) || 0
}

/** Water quantity (L) to mix with the material for the given volume. */
export const getWaterQuantityL = (
  volumeM3: number,
  material?: MaterialStat,
): number => {
  if (!material) return 0
  return ceil((rawDryMassKg(volumeM3, material) * material.waterPercent) / 100) || 0
}
